import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';

import { FormAnswers, FormSchema } from '../../shared/models/custom-form.model';

/**
 * The four PUBLIC practice-form endpoints — `tasks/patient-forms.md` §13.3.
 *
 *   GET   api/public/patient-forms/validate/:token   the five states
 *   GET   api/public/patient-forms/:token            schema + practice display fields + draft
 *   PATCH api/public/patient-forms/:token            autosave a draft
 *   PUT   api/public/patient-forms/:token            submit
 *
 * ── THE ONE RULE THIS SERVICE EXISTS TO ENFORCE ──────────────────────────────────────────────
 *
 * **No Authorization header, and no id in any body.** The patient has no account, so there is no
 * `localStorage.getItem('token')` here — unlike every authenticated service in this folder, and
 * deliberately so: a bearer token attached to a public endpoint is a session leaked into a link a
 * patient may open on a shared phone. And per §13.3 the request body carries `answers` and
 * nothing else: no `groupId`, no `patientId`, no `customFormId`, no request id. The URL token is
 * the entire address, and the server resolves everything from `sha256(token)`.
 *
 * There is consequently NO "copy link" or "resend" method on this service, and there can never be
 * one: §14.3 decision 2 — the raw token is unrecoverable by construction and a resend rotates it,
 * so a link this page could hand back would already be dead.
 *
 * ── WHY THE ERROR PATH IS NOT SWALLOWED HERE ─────────────────────────────────────────────────
 *
 * `not_found` / `completed` / `expired` / `revoked` arrive as HTTP 404 and 410 with a `status` in
 * the body. This service does NOT map them to a success value: each is a different screen with
 * different words (§5, `patientInviteController.js:713`), and a service that flattened them into
 * one `{ ok: false }` would make collapsing them into one error page the path of least resistance.
 * `readPublicState` is provided so a caller reads the state off the error rather than guessing
 * from the status code.
 */

/** §13.3. `ok` is 200; `not_found` is 404; the other three are 410. */
export type PublicFormState = 'ok' | 'not_found' | 'completed' | 'expired' | 'revoked';

/** `GET /validate/:token` — 200. Carries NO ids: the page already holds its only address. */
export interface PublicFormValidation {
  success: boolean;
  status: PublicFormState;
  message: string;
}

/**
 * The practice's display fields, exactly as `GROUP_DISPLAY_ATTRIBUTES` returns them.
 *
 * Every one is optional: `Group.about`, `practicePhone` and `practiceEmail` are nullable
 * encrypted columns and a practice that has filled in none of them is ordinary. There is no logo
 * field on `Group` anywhere in this tree, so branding is the practice's NAME and its contact
 * details — the same set the invite wizard renders its header from.
 */
export interface PublicFormPractice {
  name?: string | null;
  about?: string | null;
  practiceNumber?: string | null;
  practicePhone?: string | null;
  practiceEmail?: string | null;
  practiceOpeningTimes?: string | null;
}

/** The form's three patient-facing fields. Its configuration is deliberately not sent (§7.4). */
export interface PublicFormTemplate {
  name?: string | null;
  description?: string | null;
  requiresSignature?: boolean | null;
}

/** `GET /:token` — the `data` envelope, unwrapped. */
export interface PublicFormLoad {
  status: string;
  startedAt: string | null;
  expiresAt: string | null;
  form: PublicFormTemplate | null;
  /** The PINNED version's schema — never the template's current one (§1.2 defect 5). */
  schema: FormSchema;
  /** The saved draft, or null. Written into the renderer RAW — see §14.2 decision 6. */
  answers: FormAnswers | null;
  practice: PublicFormPractice | null;
}

/** `PATCH /:token` — 200. */
export interface PublicFormDraftSaved {
  savedAt: string;
  startedAt: string;
}

/**
 * `PUT /:token` — 200.
 *
 * `alreadyCompleted` is a SUCCESS, not an error: it means the patient's earlier submission landed
 * and this is a retry over a dropped response. The endpoint is idempotent by design
 * (claim-before-work), which is what makes retrying safe.
 */
export interface PublicFormSubmitResult {
  success: boolean;
  alreadyCompleted?: boolean;
  status: string;
  message: string;
  data?: { completedAt: string } | null;
}

/** One server-side answer error, keyed on the question it belongs to (`AnswerError`). */
export interface PublicFormAnswerError {
  questionId: string;
  message: string;
}

/** The shape of a refusal body from any of the four endpoints. */
export interface PublicFormErrorBody {
  success?: boolean;
  status?: string;
  error?: string;
  message?: string;
  errors?: PublicFormAnswerError[];
}

/**
 * Read the five-state marker off a failed call.
 *
 * Returns `null` for anything that is not one of the five — a 500, a rate-limit 429, a dropped
 * connection — because those are a DIFFERENT screen: they are retryable and the other four are
 * not. `canRetryInviteLoad` in the invite wizard draws exactly this line.
 */
export function readPublicState(error: unknown): PublicFormState | null {
  const body = (error as { error?: PublicFormErrorBody } | null)?.error;
  const status = body?.status;
  if (status === 'not_found' || status === 'completed' || status === 'expired'
    || status === 'revoked' || status === 'ok') {
    return status;
  }
  return null;
}

/** The server's message, verbatim, when it sent one. It is written for the patient, not for us. */
export function readPublicMessage(error: unknown): string | null {
  const body = (error as { error?: PublicFormErrorBody } | null)?.error;
  const message = body?.message;
  return typeof message === 'string' && message !== '' ? message : null;
}

/** The per-question errors from a 400 `VALIDATION_FAILED`, or `[]`. */
export function readAnswerErrors(error: unknown): PublicFormAnswerError[] {
  const body = (error as { error?: PublicFormErrorBody } | null)?.error;
  if (body?.error !== 'VALIDATION_FAILED' || !Array.isArray(body.errors)) return [];
  return body.errors.filter((entry): entry is PublicFormAnswerError =>
    !!entry && typeof entry.questionId === 'string' && typeof entry.message === 'string');
}

@Injectable({ providedIn: 'root' })
export class PublicPatientFormService {

  /** Relative, as every other service in this folder is — `api/...`, resolved against the host. */
  private readonly apiUrl = 'api/public/patient-forms';

  constructor(private http: HttpClient) { }

  /** Step 1, always. Establishes WHICH of the five screens the patient gets. */
  validate(token: string): Observable<PublicFormValidation> {
    return this.http.get<PublicFormValidation>(`${this.apiUrl}/validate/${encodeURIComponent(token)}`);
  }

  /** Step 2. Unwraps `data`, which is where the endpoint puts everything worth having. */
  load(token: string): Observable<PublicFormLoad> {
    return this.http
      .get<{ success: boolean; data: PublicFormLoad }>(`${this.apiUrl}/${encodeURIComponent(token)}`)
      .pipe(map((response) => response.data));
  }

  /**
   * Autosave. `answers` and nothing else.
   *
   * Server-side rather than `localStorage` — which is what the invite wizard uses, and what loses
   * a half-finished questionnaire the moment the patient moves from the phone that got the SMS to
   * the laptop they would rather type ten pages on.
   */
  saveDraft(token: string, answers: FormAnswers): Observable<PublicFormDraftSaved> {
    return this.http
      .patch<{ success: boolean; data: PublicFormDraftSaved }>(
        `${this.apiUrl}/${encodeURIComponent(token)}`, { answers })
      .pipe(map((response) => response.data));
  }

  /**
   * Submit.
   *
   * `signatureImageUrl` is passed only when the schema actually contains a signature question the
   * patient signed — the endpoint keeps it in its own column (`request.signatureImageUrl`), which
   * nothing else on the public path would ever fill. It is the patient's own answer, not an id.
   */
  submit(token: string, answers: FormAnswers, signatureImageUrl?: string | null)
    : Observable<PublicFormSubmitResult> {
    const body: { answers: FormAnswers; signatureImageUrl?: string } = { answers };
    if (typeof signatureImageUrl === 'string' && signatureImageUrl !== '') {
      body.signatureImageUrl = signatureImageUrl;
    }
    return this.http.put<PublicFormSubmitResult>(
      `${this.apiUrl}/${encodeURIComponent(token)}`, body);
  }
}
